// Max OS Cloud API Documentation Data
// Mock data for developer API reference

export const apiSections = [
  {
    id: "authentication",
    title: "Authentication",
    icon: "Lock",
    content: {
      description:
        "All API requests require authentication. Max OS Cloud supports API keys and OAuth2 Bearer tokens.",
      methods: [
        {
          type: "apiKey",
          name: "X-API-Key",
          location: "Header",
          description: "Pass your API key in the X-API-Key header.",
        },
        {
          type: "bearer",
          name: "Authorization",
          location: "Header",
          description: "Pass a Bearer token: Authorization: Bearer <token>",
        },
        {
          type: "oauth2",
          name: "OAuth2",
          location: "Authorization Code Flow",
          description:
            "Full OAuth2 authorization code flow for third-party integrations.",
        },
      ],
      curl: `curl -X GET "https://api.maxoscloud.com/v1/agents" \\
  -H "X-API-Key: your_api_key_here"`,
      javascript: `const response = await fetch("https://api.maxoscloud.com/v1/agents", {
  headers: {
    "X-API-Key": "your_api_key_here",
  },
});
const data = await response.json();`,
      python: `import requests

response = requests.get(
    "https://api.maxoscloud.com/v1/agents",
    headers={"X-API-Key": "your_api_key_here"}
)
data = response.json()`,
      response: JSON.stringify(
        {
          authenticated: true,
          user_id: "usr_abc123",
          plan: "growth",
          rate_limit: "1000/hour",
        },
        null,
        2
      ),
    },
  },
  {
    id: "agents",
    title: "Agents API",
    icon: "Bot",
    endpoints: [
      {
        method: "GET",
        path: "/api/v1/agents",
        description: "List all agents in your workspace.",
        parameters: [
          { name: "limit", type: "integer", location: "query", required: false, description: "Max results (default 20, max 100)" },
          { name: "offset", type: "integer", location: "query", required: false, description: "Pagination offset (default 0)" },
          { name: "status", type: "string", location: "query", required: false, description: "Filter by status: active, inactive, paused" },
        ],
        curl: `curl -X GET "https://api.maxoscloud.com/v1/agents?limit=20&offset=0" \\
  -H "X-API-Key: your_api_key_here"`,
        javascript: `const response = await fetch(
  "https://api.maxoscloud.com/v1/agents?limit=20&offset=0",
  { headers: { "X-API-Key": "your_api_key_here" } }
);
const data = await response.json();`,
        python: `import requests

response = requests.get(
    "https://api.maxoscloud.com/v1/agents",
    params={"limit": 20, "offset": 0},
    headers={"X-API-Key": "your_api_key_here"}
)
data = response.json()`,
        response: JSON.stringify(
          {
            data: [
              {
                id: "agt_001",
                name: "Support Agent",
                status: "active",
                channels: ["telegram", "whatsapp"],
                created_at: "2026-01-15T10:30:00Z",
              },
              {
                id: "agt_002",
                name: "Sales Bot",
                status: "active",
                channels: ["slack"],
                created_at: "2026-02-20T14:00:00Z",
              },
            ],
            total: 2,
            limit: 20,
            offset: 0,
          },
          null,
          2
        ),
        statusCodes: [
          { code: 200, description: "Success" },
          { code: 401, description: "Unauthorized — invalid API key" },
          { code: 429, description: "Rate limit exceeded" },
        ],
      },
      {
        method: "POST",
        path: "/api/v1/agents",
        description: "Create a new agent.",
        parameters: [
          { name: "name", type: "string", location: "body", required: true, description: "Agent display name" },
          { name: "model", type: "string", location: "body", required: false, description: "LLM model (default: maxos-4)" },
          { name: "system_prompt", type: "string", location: "body", required: false, description: "System instructions" },
          { name: "channels", type: "array", location: "body", required: false, description: "Channel IDs to connect" },
        ],
        curl: `curl -X POST "https://api.maxoscloud.com/v1/agents" \\
  -H "X-API-Key: your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "My Agent",
    "model": "maxos-4",
    "system_prompt": "You are a helpful assistant."
  }'`,
        javascript: `const response = await fetch("https://api.maxoscloud.com/v1/agents", {
  method: "POST",
  headers: {
    "X-API-Key": "your_api_key_here",
    "Content-Type": "application/json",
  },
  body: JSON.stringify({
    name: "My Agent",
    model: "maxos-4",
    system_prompt: "You are a helpful assistant.",
  }),
});
const data = await response.json();`,
        python: `import requests

response = requests.post(
    "https://api.maxoscloud.com/v1/agents",
    headers={
        "X-API-Key": "your_api_key_here",
        "Content-Type": "application/json",
    },
    json={
        "name": "My Agent",
        "model": "maxos-4",
        "system_prompt": "You are a helpful assistant.",
    }
)
data = response.json()`,
        response: JSON.stringify(
          {
            id: "agt_003",
            name: "My Agent",
            model: "maxos-4",
            system_prompt: "You are a helpful assistant.",
            status: "active",
            channels: [],
            created_at: "2026-06-04T09:00:00Z",
          },
          null,
          2
        ),
        statusCodes: [
          { code: 201, description: "Agent created successfully" },
          { code: 400, description: "Bad request — missing required fields" },
          { code: 401, description: "Unauthorized" },
          { code: 429, description: "Rate limit exceeded" },
        ],
      },
      {
        method: "GET",
        path: "/api/v1/agents/:id",
        description: "Get details of a specific agent.",
        parameters: [
          { name: "id", type: "string", location: "path", required: true, description: "Agent ID" },
        ],
        curl: `curl -X GET "https://api.maxoscloud.com/v1/agents/agt_001" \\
  -H "X-API-Key: your_api_key_here"`,
        javascript: `const response = await fetch(
  "https://api.maxoscloud.com/v1/agents/agt_001",
  { headers: { "X-API-Key": "your_api_key_here" } }
);
const data = await response.json();`,
        python: `import requests

response = requests.get(
    "https://api.maxoscloud.com/v1/agents/agt_001",
    headers={"X-API-Key": "your_api_key_here"}
)
data = response.json()`,
        response: JSON.stringify(
          {
            id: "agt_001",
            name: "Support Agent",
            model: "maxos-4",
            system_prompt: "You are a customer support agent.",
            status: "active",
            channels: ["telegram", "whatsapp"],
            config: {
              temperature: 0.7,
              max_tokens: 2048,
            },
            created_at: "2026-01-15T10:30:00Z",
            updated_at: "2026-05-20T08:00:00Z",
          },
          null,
          2
        ),
        statusCodes: [
          { code: 200, description: "Success" },
          { code: 401, description: "Unauthorized" },
          { code: 404, description: "Agent not found" },
        ],
      },
      {
        method: "PUT",
        path: "/api/v1/agents/:id",
        description: "Update an existing agent.",
        parameters: [
          { name: "id", type: "string", location: "path", required: true, description: "Agent ID" },
          { name: "name", type: "string", location: "body", required: false, description: "New display name" },
          { name: "model", type: "string", location: "body", required: false, description: "LLM model" },
          { name: "system_prompt", type: "string", location: "body", required: false, description: "System instructions" },
          { name: "status", type: "string", location: "body", required: false, description: "active, inactive, paused" },
        ],
        curl: `curl -X PUT "https://api.maxoscloud.com/v1/agents/agt_001" \\
  -H "X-API-Key: your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"name": "Updated Agent Name"}'`,
        javascript: `const response = await fetch(
  "https://api.maxoscloud.com/v1/agents/agt_001",
  {
    method: "PUT",
    headers: {
      "X-API-Key": "your_api_key_here",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ name: "Updated Agent Name" }),
  }
);
const data = await response.json();`,
        python: `import requests

response = requests.put(
    "https://api.maxoscloud.com/v1/agents/agt_001",
    headers={
        "X-API-Key": "your_api_key_here",
        "Content-Type": "application/json",
    },
    json={"name": "Updated Agent Name"}
)
data = response.json()`,
        response: JSON.stringify(
          {
            id: "agt_001",
            name: "Updated Agent Name",
            model: "maxos-4",
            status: "active",
            updated_at: "2026-06-04T09:30:00Z",
          },
          null,
          2
        ),
        statusCodes: [
          { code: 200, description: "Agent updated" },
          { code: 400, description: "Bad request" },
          { code: 401, description: "Unauthorized" },
          { code: 404, description: "Agent not found" },
        ],
      },
      {
        method: "DELETE",
        path: "/api/v1/agents/:id",
        description: "Delete an agent.",
        parameters: [
          { name: "id", type: "string", location: "path", required: true, description: "Agent ID" },
        ],
        curl: `curl -X DELETE "https://api.maxoscloud.com/v1/agents/agt_001" \\
  -H "X-API-Key: your_api_key_here"`,
        javascript: `const response = await fetch(
  "https://api.maxoscloud.com/v1/agents/agt_001",
  {
    method: "DELETE",
    headers: { "X-API-Key": "your_api_key_here" },
  }
);
const data = await response.json();`,
        python: `import requests

response = requests.delete(
    "https://api.maxoscloud.com/v1/agents/agt_001",
    headers={"X-API-Key": "your_api_key_here"}
)
data = response.json()`,
        response: JSON.stringify(
          { success: true, message: "Agent agt_001 deleted" },
          null,
          2
        ),
        statusCodes: [
          { code: 200, description: "Agent deleted" },
          { code: 401, description: "Unauthorized" },
          { code: 404, description: "Agent not found" },
        ],
      },
    ],
  },
  {
    id: "messages",
    title: "Messages API",
    icon: "MessageSquare",
    endpoints: [
      {
        method: "POST",
        path: "/api/v1/messages",
        description: "Send a message through an agent.",
        parameters: [
          { name: "agent_id", type: "string", location: "body", required: true, description: "Agent ID" },
          { name: "channel_id", type: "string", location: "body", required: true, description: "Channel ID" },
          { name: "content", type: "string", location: "body", required: true, description: "Message content" },
          { name: "recipient", type: "string", location: "body", required: true, description: "Recipient identifier" },
        ],
        curl: `curl -X POST "https://api.maxoscloud.com/v1/messages" \\
  -H "X-API-Key: your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "agent_id": "agt_001",
    "channel_id": "ch_telegram_001",
    "content": "Hello! How can I help?",
    "recipient": "+1234567890"
  }'`,
        javascript: `const response = await fetch("https://api.maxoscloud.com/v1/messages", {
  method: "POST",
  headers: {
    "X-API-Key": "your_api_key_here",
    "Content-Type": "application/json",
  },
  body: JSON.stringify({
    agent_id: "agt_001",
    channel_id: "ch_telegram_001",
    content: "Hello! How can I help?",
    recipient: "+1234567890",
  }),
});
const data = await response.json();`,
        python: `import requests

response = requests.post(
    "https://api.maxoscloud.com/v1/messages",
    headers={
        "X-API-Key": "your_api_key_here",
        "Content-Type": "application/json",
    },
    json={
        "agent_id": "agt_001",
        "channel_id": "ch_telegram_001",
        "content": "Hello! How can I help?",
        "recipient": "+1234567890",
    }
)
data = response.json()`,
        response: JSON.stringify(
          {
            id: "msg_789",
            agent_id: "agt_001",
            channel_id: "ch_telegram_001",
            content: "Hello! How can I help?",
            direction: "outbound",
            status: "sent",
            created_at: "2026-06-04T09:15:00Z",
          },
          null,
          2
        ),
        statusCodes: [
          { code: 201, description: "Message sent" },
          { code: 400, description: "Bad request" },
          { code: 401, description: "Unauthorized" },
          { code: 429, description: "Rate limit exceeded" },
        ],
      },
      {
        method: "GET",
        path: "/api/v1/messages",
        description: "List messages with optional filters.",
        parameters: [
          { name: "agent_id", type: "string", location: "query", required: false, description: "Filter by agent" },
          { name: "channel_id", type: "string", location: "query", required: false, description: "Filter by channel" },
          { name: "limit", type: "integer", location: "query", required: false, description: "Max results (default 20)" },
          { name: "offset", type: "integer", location: "query", required: false, description: "Pagination offset" },
        ],
        curl: `curl -X GET "https://api.maxoscloud.com/v1/messages?agent_id=agt_001&limit=20" \\
  -H "X-API-Key: your_api_key_here"`,
        javascript: `const response = await fetch(
  "https://api.maxoscloud.com/v1/messages?agent_id=agt_001&limit=20",
  { headers: { "X-API-Key": "your_api_key_here" } }
);
const data = await response.json();`,
        python: `import requests

response = requests.get(
    "https://api.maxoscloud.com/v1/messages",
    params={"agent_id": "agt_001", "limit": 20},
    headers={"X-API-Key": "your_api_key_here"}
)
data = response.json()`,
        response: JSON.stringify(
          {
            data: [
              {
                id: "msg_789",
                agent_id: "agt_001",
                content: "Hello! How can I help?",
                direction: "outbound",
                status: "delivered",
                created_at: "2026-06-04T09:15:00Z",
              },
              {
                id: "msg_788",
                agent_id: "agt_001",
                content: "I need help with my order",
                direction: "inbound",
                status: "received",
                created_at: "2026-06-04T09:14:00Z",
              },
            ],
            total: 2,
            limit: 20,
            offset: 0,
          },
          null,
          2
        ),
        statusCodes: [
          { code: 200, description: "Success" },
          { code: 401, description: "Unauthorized" },
        ],
      },
      {
        method: "GET",
        path: "/api/v1/messages/:id",
        description: "Get details of a specific message.",
        parameters: [
          { name: "id", type: "string", location: "path", required: true, description: "Message ID" },
        ],
        curl: `curl -X GET "https://api.maxoscloud.com/v1/messages/msg_789" \\
  -H "X-API-Key: your_api_key_here"`,
        javascript: `const response = await fetch(
  "https://api.maxoscloud.com/v1/messages/msg_789",
  { headers: { "X-API-Key": "your_api_key_here" } }
);
const data = await response.json();`,
        python: `import requests

response = requests.get(
    "https://api.maxoscloud.com/v1/messages/msg_789",
    headers={"X-API-Key": "your_api_key_here"}
)
data = response.json()`,
        response: JSON.stringify(
          {
            id: "msg_789",
            agent_id: "agt_001",
            channel_id: "ch_telegram_001",
            content: "Hello! How can I help?",
            direction: "outbound",
            status: "delivered",
            recipient: "+1234567890",
            created_at: "2026-06-04T09:15:00Z",
            delivered_at: "2026-06-04T09:15:02Z",
          },
          null,
          2
        ),
        statusCodes: [
          { code: 200, description: "Success" },
          { code: 401, description: "Unauthorized" },
          { code: 404, description: "Message not found" },
        ],
      },
    ],
  },
  {
    id: "channels",
    title: "Channels API",
    icon: "Globe",
    endpoints: [
      {
        method: "GET",
        path: "/api/v1/channels",
        description: "List all connected channels.",
        parameters: [
          { name: "type", type: "string", location: "query", required: false, description: "Filter by type: telegram, whatsapp, slack, discord" },
          { name: "status", type: "string", location: "query", required: false, description: "Filter by status: connected, disconnected" },
        ],
        curl: `curl -X GET "https://api.maxoscloud.com/v1/channels" \\
  -H "X-API-Key: your_api_key_here"`,
        javascript: `const response = await fetch(
  "https://api.maxoscloud.com/v1/channels",
  { headers: { "X-API-Key": "your_api_key_here" } }
);
const data = await response.json();`,
        python: `import requests

response = requests.get(
    "https://api.maxoscloud.com/v1/channels",
    headers={"X-API-Key": "your_api_key_here"}
)
data = response.json()`,
        response: JSON.stringify(
          {
            data: [
              {
                id: "ch_telegram_001",
                type: "telegram",
                name: "Main Bot",
                status: "connected",
                connected_at: "2026-01-15T10:30:00Z",
              },
              {
                id: "ch_slack_001",
                type: "slack",
                name: "Engineering",
                status: "connected",
                connected_at: "2026-02-01T08:00:00Z",
              },
            ],
            total: 2,
          },
          null,
          2
        ),
        statusCodes: [
          { code: 200, description: "Success" },
          { code: 401, description: "Unauthorized" },
        ],
      },
      {
        method: "POST",
        path: "/api/v1/channels",
        description: "Connect a new channel.",
        parameters: [
          { name: "type", type: "string", location: "body", required: true, description: "Channel type: telegram, whatsapp, slack, discord" },
          { name: "name", type: "string", location: "body", required: true, description: "Display name" },
          { name: "credentials", type: "object", location: "body", required: true, description: "Channel-specific credentials" },
        ],
        curl: `curl -X POST "https://api.maxoscloud.com/v1/channels" \\
  -H "X-API-Key: your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "type": "telegram",
    "name": "Support Bot",
    "credentials": {"bot_token": "YOUR_BOT_TOKEN"}
  }'`,
        javascript: `const response = await fetch("https://api.maxoscloud.com/v1/channels", {
  method: "POST",
  headers: {
    "X-API-Key": "your_api_key_here",
    "Content-Type": "application/json",
  },
  body: JSON.stringify({
    type: "telegram",
    name: "Support Bot",
    credentials: { bot_token: "YOUR_BOT_TOKEN" },
  }),
});
const data = await response.json();`,
        python: `import requests

response = requests.post(
    "https://api.maxoscloud.com/v1/channels",
    headers={
        "X-API-Key": "your_api_key_here",
        "Content-Type": "application/json",
    },
    json={
        "type": "telegram",
        "name": "Support Bot",
        "credentials": {"bot_token": "YOUR_BOT_TOKEN"},
    }
)
data = response.json()`,
        response: JSON.stringify(
          {
            id: "ch_telegram_002",
            type: "telegram",
            name: "Support Bot",
            status: "connected",
            connected_at: "2026-06-04T10:00:00Z",
          },
          null,
          2
        ),
        statusCodes: [
          { code: 201, description: "Channel connected" },
          { code: 400, description: "Bad request — invalid credentials" },
          { code: 401, description: "Unauthorized" },
        ],
      },
      {
        method: "DELETE",
        path: "/api/v1/channels/:id",
        description: "Disconnect a channel.",
        parameters: [
          { name: "id", type: "string", location: "path", required: true, description: "Channel ID" },
        ],
        curl: `curl -X DELETE "https://api.maxoscloud.com/v1/channels/ch_telegram_001" \\
  -H "X-API-Key: your_api_key_here"`,
        javascript: `const response = await fetch(
  "https://api.maxoscloud.com/v1/channels/ch_telegram_001",
  {
    method: "DELETE",
    headers: { "X-API-Key": "your_api_key_here" },
  }
);
const data = await response.json();`,
        python: `import requests

response = requests.delete(
    "https://api.maxoscloud.com/v1/channels/ch_telegram_001",
    headers={"X-API-Key": "your_api_key_here"}
)
data = response.json()`,
        response: JSON.stringify(
          { success: true, message: "Channel ch_telegram_001 disconnected" },
          null,
          2
        ),
        statusCodes: [
          { code: 200, description: "Channel disconnected" },
          { code: 401, description: "Unauthorized" },
          { code: 404, description: "Channel not found" },
        ],
      },
    ],
  },
  {
    id: "analytics",
    title: "Analytics API",
    icon: "BarChart3",
    endpoints: [
      {
        method: "GET",
        path: "/api/v1/analytics/overview",
        description: "Get general metrics overview.",
        parameters: [
          { name: "from", type: "string", location: "query", required: false, description: "Start date (ISO 8601)" },
          { name: "to", type: "string", location: "query", required: false, description: "End date (ISO 8601)" },
        ],
        curl: `curl -X GET "https://api.maxoscloud.com/v1/analytics/overview?from=2026-06-01&to=2026-06-04" \\
  -H "X-API-Key: your_api_key_here"`,
        javascript: `const response = await fetch(
  "https://api.maxoscloud.com/v1/analytics/overview?from=2026-06-01&to=2026-06-04",
  { headers: { "X-API-Key": "your_api_key_here" } }
);
const data = await response.json();`,
        python: `import requests

response = requests.get(
    "https://api.maxoscloud.com/v1/analytics/overview",
    params={"from": "2026-06-01", "to": "2026-06-04"},
    headers={"X-API-Key": "your_api_key_here"}
)
data = response.json()`,
        response: JSON.stringify(
          {
            period: { from: "2026-06-01", to: "2026-06-04" },
            messages: { total: 12450, inbound: 6800, outbound: 5650 },
            agents: { total: 5, active: 4 },
            channels: { total: 3, connected: 3 },
            avg_response_time_ms: 340,
            satisfaction_score: 4.7,
          },
          null,
          2
        ),
        statusCodes: [
          { code: 200, description: "Success" },
          { code: 401, description: "Unauthorized" },
        ],
      },
      {
        method: "GET",
        path: "/api/v1/analytics/messages",
        description: "Get message analytics data.",
        parameters: [
          { name: "from", type: "string", location: "query", required: false, description: "Start date" },
          { name: "to", type: "string", location: "query", required: false, description: "End date" },
          { name: "granularity", type: "string", location: "query", required: false, description: "hourly, daily, weekly" },
        ],
        curl: `curl -X GET "https://api.maxoscloud.com/v1/analytics/messages?granularity=daily" \\
  -H "X-API-Key: your_api_key_here"`,
        javascript: `const response = await fetch(
  "https://api.maxoscloud.com/v1/analytics/messages?granularity=daily",
  { headers: { "X-API-Key": "your_api_key_here" } }
);
const data = await response.json();`,
        python: `import requests

response = requests.get(
    "https://api.maxoscloud.com/v1/analytics/messages",
    params={"granularity": "daily"},
    headers={"X-API-Key": "your_api_key_here"}
)
data = response.json()`,
        response: JSON.stringify(
          {
            granularity: "daily",
            data: [
              { date: "2026-06-01", inbound: 320, outbound: 280 },
              { date: "2026-06-02", inbound: 410, outbound: 350 },
              { date: "2026-06-03", inbound: 380, outbound: 310 },
              { date: "2026-06-04", inbound: 290, outbound: 250 },
            ],
          },
          null,
          2
        ),
        statusCodes: [
          { code: 200, description: "Success" },
          { code: 401, description: "Unauthorized" },
        ],
      },
      {
        method: "GET",
        path: "/api/v1/analytics/channels",
        description: "Get analytics data per channel.",
        parameters: [
          { name: "from", type: "string", location: "query", required: false, description: "Start date" },
          { name: "to", type: "string", location: "query", required: false, description: "End date" },
        ],
        curl: `curl -X GET "https://api.maxoscloud.com/v1/analytics/channels" \\
  -H "X-API-Key: your_api_key_here"`,
        javascript: `const response = await fetch(
  "https://api.maxoscloud.com/v1/analytics/channels",
  { headers: { "X-API-Key": "your_api_key_here" } }
);
const data = await response.json();`,
        python: `import requests

response = requests.get(
    "https://api.maxoscloud.com/v1/analytics/channels",
    headers={"X-API-Key": "your_api_key_here"}
)
data = response.json()`,
        response: JSON.stringify(
          {
            data: [
              {
                channel_id: "ch_telegram_001",
                channel_type: "telegram",
                messages: 5200,
                avg_response_time_ms: 280,
              },
              {
                channel_id: "ch_slack_001",
                channel_type: "slack",
                messages: 3100,
                avg_response_time_ms: 420,
              },
            ],
          },
          null,
          2
        ),
        statusCodes: [
          { code: 200, description: "Success" },
          { code: 401, description: "Unauthorized" },
        ],
      },
    ],
  },
  {
    id: "webhooks",
    title: "Webhooks",
    icon: "Webhook",
    content: {
      description:
        "Configure webhooks to receive real-time events from Max OS Cloud.",
      configuration: {
        curl: `curl -X POST "https://api.maxoscloud.com/v1/webhooks" \\
  -H "X-API-Key: your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "url": "https://your-server.com/webhook",
    "events": ["message.received", "agent.created"],
    "secret": "your_signing_secret"
  }'`,
        javascript: `const response = await fetch("https://api.maxoscloud.com/v1/webhooks", {
  method: "POST",
  headers: {
    "X-API-Key": "your_api_key_here",
    "Content-Type": "application/json",
  },
  body: JSON.stringify({
    url: "https://your-server.com/webhook",
    events: ["message.received", "agent.created"],
    secret: "your_signing_secret",
  }),
});
const data = await response.json();`,
        python: `import requests

response = requests.post(
    "https://api.maxoscloud.com/v1/webhooks",
    headers={
        "X-API-Key": "your_api_key_here",
        "Content-Type": "application/json",
    },
    json={
        "url": "https://your-server.com/webhook",
        "events": ["message.received", "agent.created"],
        "secret": "your_signing_secret",
    }
)
data = response.json()`,
      },
      events: [
        {
          name: "message.received",
          description: "Triggered when a new inbound message is received.",
          payload: JSON.stringify(
            {
              event: "message.received",
              timestamp: "2026-06-04T09:15:00Z",
              data: {
                message_id: "msg_788",
                agent_id: "agt_001",
                channel_id: "ch_telegram_001",
                content: "I need help with my order",
                sender: { id: "+1234567890", name: "John Doe" },
              },
            },
            null,
            2
          ),
        },
        {
          name: "message.sent",
          description: "Triggered when an outbound message is sent.",
          payload: JSON.stringify(
            {
              event: "message.sent",
              timestamp: "2026-06-04T09:15:02Z",
              data: {
                message_id: "msg_789",
                agent_id: "agt_001",
                channel_id: "ch_telegram_001",
                content: "Hello! How can I help?",
                recipient: "+1234567890",
              },
            },
            null,
            2
          ),
        },
        {
          name: "agent.created",
          description: "Triggered when a new agent is created.",
          payload: JSON.stringify(
            {
              event: "agent.created",
              timestamp: "2026-06-04T09:00:00Z",
              data: {
                agent_id: "agt_003",
                name: "My Agent",
                model: "maxos-4",
              },
            },
            null,
            2
          ),
        },
        {
          name: "agent.updated",
          description: "Triggered when an agent is updated.",
          payload: JSON.stringify(
            {
              event: "agent.updated",
              timestamp: "2026-06-04T09:30:00Z",
              data: {
                agent_id: "agt_001",
                changes: { name: "Updated Agent Name" },
              },
            },
            null,
            2
          ),
        },
        {
          name: "channel.connected",
          description: "Triggered when a channel is connected.",
          payload: JSON.stringify(
            {
              event: "channel.connected",
              timestamp: "2026-06-04T10:00:00Z",
              data: {
                channel_id: "ch_telegram_002",
                type: "telegram",
                name: "Support Bot",
              },
            },
            null,
            2
          ),
        },
        {
          name: "channel.disconnected",
          description: "Triggered when a channel is disconnected.",
          payload: JSON.stringify(
            {
              event: "channel.disconnected",
              timestamp: "2026-06-04T11:00:00Z",
              data: {
                channel_id: "ch_telegram_001",
                type: "telegram",
                reason: "manual",
              },
            },
            null,
            2
          ),
        },
      ],
    },
  },
  {
    id: "sdks",
    title: "SDKs",
    icon: "Package",
    content: {
      description:
        "Official SDKs for popular languages. All SDKs are open source and community-maintained.",
      sdks: [
        {
          name: "Python",
          package: "maxoscloud",
          install: "pip install maxoscloud",
          url: "https://github.com/maxoscloud/python-sdk",
          example: `from maxoscloud import Client

client = Client(api_key="your_api_key")

# List agents
agents = client.agents.list()

# Send a message
message = client.messages.create(
    agent_id="agt_001",
    channel_id="ch_telegram_001",
    content="Hello!",
    recipient="+1234567890"
)`,
        },
        {
          name: "JavaScript",
          package: "@maxoscloud/sdk",
          install: "npm install @maxoscloud/sdk",
          url: "https://github.com/maxoscloud/js-sdk",
          example: `import { Client } from "@maxoscloud/sdk";

const client = new Client({ apiKey: "your_api_key" });

// List agents
const agents = await client.agents.list();

// Send a message
const message = await client.messages.create({
  agent_id: "agt_001",
  channel_id: "ch_telegram_001",
  content: "Hello!",
  recipient: "+1234567890",
});`,
        },
        {
          name: "Ruby",
          package: "maxoscloud-ruby",
          install: "gem install maxoscloud-ruby",
          url: "https://github.com/maxoscloud/ruby-sdk",
          example: `require "maxoscloud"

client = MaxOSCloud::Client.new(api_key: "your_api_key")

# List agents
agents = client.agents.list

# Send a message
message = client.messages.create(
  agent_id: "agt_001",
  channel_id: "ch_telegram_001",
  content: "Hello!",
  recipient: "+1234567890"
)`,
        },
      ],
    },
  },
  {
    id: "rate-limiting",
    title: "Rate Limiting",
    icon: "Zap",
    content: {
      description:
        "API requests are rate-limited based on your plan. Rate limit headers are included in all responses.",
      limits: [
        { plan: "Starter", requests: "100/hour", burst: "10/second" },
        { plan: "Growth", requests: "1,000/hour", burst: "50/second" },
        { plan: "Enterprise", requests: "10,000/hour", burst: "200/second" },
      ],
      headers: [
        { name: "X-RateLimit-Limit", description: "Maximum requests per window" },
        { name: "X-RateLimit-Remaining", description: "Requests remaining in current window" },
        { name: "X-RateLimit-Reset", description: "Unix timestamp when the window resets" },
      ],
      curl: `curl -I "https://api.maxoscloud.com/v1/agents" \\
  -H "X-API-Key: your_api_key_here"

# Response Headers:
# X-RateLimit-Limit: 1000
# X-RateLimit-Remaining: 999
# X-RateLimit-Reset: 1759574400`,
    },
  },
  {
    id: "error-codes",
    title: "Error Codes",
    icon: "AlertTriangle",
    content: {
      description:
        "All errors follow a consistent format. The response body includes an error code and message.",
      errorFormat: JSON.stringify(
        {
          error: {
            code: "invalid_request",
            message: "The request body is missing required fields.",
            details: [{ field: "name", message: "This field is required" }],
          },
        },
        null,
        2
      ),
      codes: [
        { code: 400, name: "Bad Request", description: "Invalid request body or parameters" },
        { code: 401, name: "Unauthorized", description: "Invalid or missing API key" },
        { code: 403, name: "Forbidden", description: "Insufficient permissions for this resource" },
        { code: 404, name: "Not Found", description: "Resource does not exist" },
        { code: 409, name: "Conflict", description: "Resource already exists or state conflict" },
        { code: 422, name: "Unprocessable Entity", description: "Validation error" },
        { code: 429, name: "Too Many Requests", description: "Rate limit exceeded. Retry after the time specified in Retry-After header" },
        { code: 500, name: "Internal Server Error", description: "Unexpected server error. Contact support if persistent" },
        { code: 503, name: "Service Unavailable", description: "Temporary maintenance. Retry with exponential backoff" },
      ],
    },
  },
  {
    id: "automations",
    title: "Automations API",
    icon: "Workflow",
    endpoints: [
      {
        method: "GET",
        path: "/api/v1/automations",
        description: "List all automation workflows.",
        parameters: [
          { name: "limit", type: "integer", location: "query", required: false, description: "Max results (default 20, max 100)" },
          { name: "status", type: "string", location: "query", required: false, description: "Filter by status: active, paused, draft" },
        ],
        curl: `curl -X GET "https://api.maxoscloud.com/v1/automations?limit=20" \\\n  -H "X-API-Key: your_api_key_here"`,
        javascript: `const response = await fetch(\n  "https://api.maxoscloud.com/v1/automations?limit=20",\n  { headers: { "X-API-Key": "your_api_key_here" } }\n);\nconst data = await response.json();`,
        python: `import requests\n\nresponse = requests.get(\n    "https://api.maxoscloud.com/v1/automations",\n    params={"limit": 20},\n    headers={"X-API-Key": "your_api_key_here"}\n)\ndata = response.json()`,
        response: JSON.stringify(
          {
            data: [
              {
                id: "auto_001",
                name: "Welcome Message",
                status: "active",
                trigger: "message.received",
                actions: ["send_message", "tag_conversation"],
                created_at: "2026-03-10T12:00:00Z",
              },
              {
                id: "auto_002",
                name: "Escalation Flow",
                status: "active",
                trigger: "sentiment.negative",
                actions: ["notify_human", "tag_urgent"],
                created_at: "2026-04-05T09:00:00Z",
              },
            ],
            total: 2,
            limit: 20,
            offset: 0,
          },
          null,
          2
        ),
        statusCodes: [
          { code: 200, description: "Success" },
          { code: 401, description: "Unauthorized" },
        ],
      },
      {
        method: "POST",
        path: "/api/v1/automations",
        description: "Create a new automation workflow.",
        parameters: [
          { name: "name", type: "string", location: "body", required: true, description: "Automation name" },
          { name: "trigger", type: "string", location: "body", required: true, description: "Trigger event name" },
          { name: "actions", type: "array", location: "body", required: true, description: "List of action objects" },
          { name: "enabled", type: "boolean", location: "body", required: false, description: "Enable immediately (default: true)" },
        ],
        curl: `curl -X POST "https://api.maxoscloud.com/v1/automations" \\\n  -H "X-API-Key: your_api_key_here" \\\n  -H "Content-Type: application/json" \\\n  -d '{\n    "name": "Auto Reply",\n    "trigger": "message.received",\n    "actions": [{"type": "send_message", "config": {"template": "Thanks for reaching out!"}}]\n  }'`,
        javascript: `const response = await fetch("https://api.maxoscloud.com/v1/automations", {\n  method: "POST",\n  headers: {\n    "X-API-Key": "your_api_key_here",\n    "Content-Type": "application/json",\n  },\n  body: JSON.stringify({\n    name: "Auto Reply",\n    trigger: "message.received",\n    actions: [{ type: "send_message", config: { template: "Thanks!" } }],\n  }),\n});`,
        python: `import requests\n\nresponse = requests.post(\n    "https://api.maxoscloud.com/v1/automations",\n    headers={"X-API-Key": "your_api_key_here", "Content-Type": "application/json"},\n    json={\n        "name": "Auto Reply",\n        "trigger": "message.received",\n        "actions": [{"type": "send_message", "config": {"template": "Thanks!"}}],\n    }\n)`,
        response: JSON.stringify(
          {
            id: "auto_003",
            name: "Auto Reply",
            status: "active",
            trigger: "message.received",
            actions: [{ type: "send_message", config: { template: "Thanks!" } }],
            created_at: "2026-06-04T11:00:00Z",
          },
          null,
          2
        ),
        statusCodes: [
          { code: 201, description: "Automation created" },
          { code: 400, description: "Bad request — invalid trigger or actions" },
          { code: 401, description: "Unauthorized" },
        ],
      },
      {
        method: "PUT",
        path: "/api/v1/automations/:id",
        description: "Update an existing automation.",
        parameters: [
          { name: "id", type: "string", location: "path", required: true, description: "Automation ID" },
          { name: "name", type: "string", location: "body", required: false, description: "New name" },
          { name: "actions", type: "array", location: "body", required: false, description: "Updated actions" },
          { name: "enabled", type: "boolean", location: "body", required: false, description: "Enable or disable" },
        ],
        curl: `curl -X PUT "https://api.maxoscloud.com/v1/automations/auto_001" \\\n  -H "X-API-Key: your_api_key_here" \\\n  -H "Content-Type: application/json" \\\n  -d '{"enabled": false}'`,
        javascript: `const response = await fetch(\n  "https://api.maxoscloud.com/v1/automations/auto_001",\n  {\n    method: "PUT",\n    headers: { "X-API-Key": "your_api_key_here", "Content-Type": "application/json" },\n    body: JSON.stringify({ enabled: false }),\n  }\n);`,
        python: `response = requests.put(\n    "https://api.maxoscloud.com/v1/automations/auto_001",\n    headers={"X-API-Key": "your_api_key_here"},\n    json={"enabled": False}\n)`,
        response: JSON.stringify(
          { id: "auto_001", name: "Welcome Message", status: "paused", updated_at: "2026-06-04T11:30:00Z" },
          null,
          2
        ),
        statusCodes: [
          { code: 200, description: "Automation updated" },
          { code: 401, description: "Unauthorized" },
          { code: 404, description: "Automation not found" },
        ],
      },
      {
        method: "DELETE",
        path: "/api/v1/automations/:id",
        description: "Delete an automation.",
        parameters: [
          { name: "id", type: "string", location: "path", required: true, description: "Automation ID" },
        ],
        curl: `curl -X DELETE "https://api.maxoscloud.com/v1/automations/auto_001" \\\n  -H "X-API-Key: your_api_key_here"`,
        javascript: `await fetch("https://api.maxoscloud.com/v1/automations/auto_001", {\n  method: "DELETE",\n  headers: { "X-API-Key": "your_api_key_here" },\n});`,
        python: `response = requests.delete(\n    "https://api.maxoscloud.com/v1/automations/auto_001",\n    headers={"X-API-Key": "your_api_key_here"}\n)`,
        response: JSON.stringify({ success: true, message: "Automation auto_001 deleted" }, null, 2),
        statusCodes: [
          { code: 200, description: "Automation deleted" },
          { code: 401, description: "Unauthorized" },
          { code: 404, description: "Automation not found" },
        ],
      },
    ],
  },
];

export const sidebarNav = [
  { id: "authentication", label: "Authentication" },
  { id: "agents", label: "Agents API" },
  { id: "messages", label: "Messages API" },
  { id: "channels", label: "Channels API" },
  { id: "automations", label: "Automations API" },
  { id: "analytics", label: "Analytics API" },
  { id: "webhooks", label: "Webhooks" },
  { id: "sdks", label: "SDKs" },
  { id: "rate-limiting", label: "Rate Limiting" },
  { id: "error-codes", label: "Error Codes" },
];
